import styled from 'styled-components'

import { cardFrameCSS } from '@/@bothrs-ui/web/components/card/Card'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { StIconBadge } from '../IconBadge.styled'

import type { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { ComponentPropsWithoutRef } from 'react'

/* ------------------------------------------------------------------------------------------------- *
 * LocationCard
 * ------------------------------------------------------------------------------------------------- */

type LocationCardRootProps = ComponentPropsWithoutRef<'div'>

const LocationCardRoot = ({ children, ...props }: LocationCardRootProps) => (
  <StLocationCard {...props}>{children}</StLocationCard>
)

/* ------------------------------------------------------------------------------------------------- *
 * LocationCard.Navigate
 * ------------------------------------------------------------------------------------------------- */

// A button stretched over the whole card, rendered as its last child. It stays empty and is named
// by the aria-label a call-site passes, so the card keeps plain content and the button keeps a valid
// content model. The root stays a div whether or not it is rendered.
type LocationCardNavigateProps = Omit<ComponentPropsWithoutRef<'button'>, 'children'>

const LocationCardNavigate = (props: LocationCardNavigateProps) => <StLocationNavigate type='button' {...props} />

/* ------------------------------------------------------------------------------------------------- *
 * LocationCard.Media
 * ------------------------------------------------------------------------------------------------- */

// The photo strip at the top of the card. A card without a photo has no strip: the design keeps
// the card to its content, so the call-site renders Media only when it has an image to show.
type LocationCardMediaProps = ComponentPropsWithoutRef<'div'>

const LocationCardMedia = ({ children, ...props }: LocationCardMediaProps) => (
  <StLocationMedia {...props}>{children}</StLocationMedia>
)

/* ------------------------------------------------------------------------------------------------- *
 * LocationCard.Image
 * ------------------------------------------------------------------------------------------------- */

type LocationCardImageProps = ComponentPropsWithoutRef<'img'>

const LocationCardImage = (props: LocationCardImageProps) => <StLocationImage {...props} />

/* ------------------------------------------------------------------------------------------------- *
 * LocationCard.Content
 * ------------------------------------------------------------------------------------------------- */

// The padded row under the media: text on the left, an optional affordance on the right.
type LocationCardContentProps = ComponentPropsWithoutRef<'div'>

const LocationCardContent = ({ children, ...props }: LocationCardContentProps) => (
  <StLocationContent {...props}>{children}</StLocationContent>
)

/* ------------------------------------------------------------------------------------------------- *
 * LocationCard.Name
 * ------------------------------------------------------------------------------------------------- */

// The text parts take the Span's own props, so a call site can pick another type or colour.
type LocationCardTextProps = Omit<ComponentPropsWithoutRef<typeof Span>, 'type'> & {
  type?: ComponentPropsWithoutRef<typeof Span>['type']
}

type LocationCardNameProps = LocationCardTextProps

const LocationCardName = ({ children, ...props }: LocationCardNameProps) => (
  <StText type='BodyLargeRegular' {...props}>
    {children}
  </StText>
)

/* ------------------------------------------------------------------------------------------------- *
 * LocationCard.Icon
 * ------------------------------------------------------------------------------------------------- */

type LocationCardIconProps = ComponentPropsWithoutRef<typeof FontAwesomeIcon>

const LocationCardIcon = (props: LocationCardIconProps) => <StNavigateIcon {...props} />

/* ------------------------------------------------------------------------------------------------- *
 * LocationCard.Address
 * ------------------------------------------------------------------------------------------------- */

type LocationCardAddressProps = LocationCardTextProps

const LocationCardAddress = ({ children, ...props }: LocationCardAddressProps) => (
  <StText type='BodyLargeRegular' {...props}>
    {children}
  </StText>
)

const LocationCard = Object.assign(LocationCardRoot, {
  Navigate: LocationCardNavigate,
  Media: LocationCardMedia,
  Image: LocationCardImage,
  Content: LocationCardContent,
  Name: LocationCardName,
  Address: LocationCardAddress,
  Icon: LocationCardIcon,
})

export { LocationCard }

/* ------------------------------------------------------------------------------------------------- *
 * Styled
 * ------------------------------------------------------------------------------------------------- */

// The frame has no padding of its own so the media can run edge to edge; Content pads the text.
// cardFrameCSS has no zero padding step, so the frame's padding is reset here. It is positioned so
// the Navigate overlay can stretch over it. The overflow clips the photo to the rounded corners,
// but it also lets a scrolling flex column shrink the card below its content, so shrinking is off.
const StLocationCard = styled.div`
  ${cardFrameCSS()}
  position: relative;
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 100%;
`

const StLocationNavigate = styled.button`
  ${({ theme }) => `
    position: absolute;
    inset: 0;
    margin: 0;
    padding: 0;
    border: 0;
    background: none;
    border-radius: ${theme.UI.BorderRadius.Large}px;
    cursor: pointer;

    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px ${theme.Colors.Theme.Primary?.[700] ?? theme.Colors.Theme.Primary ?? '#022248'};
    }
  `}
`

const MEDIA_HEIGHT_PX = 156

// The background shows until the photo has loaded.
const StLocationMedia = styled.div`
  ${({ theme }) => `
    position: relative;
    height: ${MEDIA_HEIGHT_PX}px;
    background-color: ${theme.Colors.Neutrals.Lighter};
  `}
`

const StLocationImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

// Fills whatever height the card has, so next to a taller card the text stays at the top while
// the navigate badge sits in the bottom corner.
const StLocationContent = styled.div`
  ${({ theme }) => `
    flex: 1;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${theme.UI.SpacingPx.Space3};
    padding: ${theme.UI.SpacingPx.Space4};
  `}
`

const StNavigateIcon = styled(StIconBadge)`
  align-self: flex-end;
`

const StText = styled(Span)`
  color: ${({ theme, color }) => color ?? theme.Colors.Neutrals.Darker};
`
